import { Inject, Injectable, signal } from '@angular/core';
import { HttpClient } from "@angular/common/http";
import { TranslateService } from "@ngx-translate/core";
import { catchError, map, Observable, of } from "rxjs";
import { DOCUMENT } from "@angular/common";
import { ConfigService } from "./config.service";

@Injectable({
  providedIn: 'root'
})
export class LanguageService {

  /** todo ADD arabic language
   * - 'ar' is not between the language options for not only landing brands => defaults to english
   * - Step 1: 'ar' currentLang until not logged in
   * - translations
   * - fix RTL mode
   * - check if backoffice accepts arabic (min-reg vs reg-step1 component)
   * - Step 2:
   */

  languageOptions = [
    { value: "en", label: "English", height: 15 },
    { value: "de", label: "Deutsch", height: 12 },
    { value: "fr", label: "Français", height: 12.5 },
    { value: "es", label: "Español", height: 15 },
    { value: "it", label: "Italiano", height: 15 },
    { value: "pt", label: "Português", height: 13 },
    { value: "ja", label: "日本語", height: 13 },
    // {value: "ar", label: "العربية", height: 13},
  ]

  // supported languages (on Rival, as locale) - no ar/ja
  supportedLanguages = ['en', 'es', 'de', 'fr', 'pt', 'it'];

  currentLang = signal<string>('bla')

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    @Inject(DOCUMENT) private document: Document,
    private configs: ConfigService
  ) {
    if (this.configs.hasOnlyLandings) {
      this.languageOptions.push({ value: "ar", label: "العربية", height: 13 })
    }

  }


  initializeLanguage(): void {
    // this.translate.setDefaultLang('en'); //not needed as we set language by translate.use() in every scenario

    /**
     set language at the initialization by going through these options
     - from localStorage: selected by user
     - from browser
     - from cloudflare
     - default: english
     */

    let savedLang: string | null = null;
    try {
      if (typeof window !== 'undefined' && localStorage) {
        savedLang = localStorage.getItem('lang');
      }
    } catch (e) {
      console.warn('localStorage is not accessible:', e);
    }

    if (savedLang) {
      this.checkAndSetSelectedLanguage(savedLang);
    } else {
      if (navigator.language) {
        const lang = navigator.language.slice(0, 2)
        this.checkAndSetSelectedLanguage(lang)
      } else {
        // no browser language available
        this.setLangFromCloudflare();
      }
    }
  }

  //setting language at initialization
  checkAndSetSelectedLanguage(lang: string): void {
    const isLanguageSupported = this.languageOptions.some(option => option.value === lang);
    if (isLanguageSupported) {
      this.currentLang.set(lang);
      //todo rtl design on core
      // if(this.configs.hasOnlyLandings){
      //   let htmlTag = this.document.getElementsByTagName('html')[0] as HTMLHtmlElement;
      //   htmlTag.dir = lang === 'ar' ? 'rtl' : 'ltr';
      // }
    } else {
      this.currentLang.set('en');
      // console.log(`${lang} is not supported. Defaulting to 'en'.`);
    }
    this.translate.use(this.currentLang());

    try {
      localStorage.setItem('lang', this.currentLang());
    } catch (error) {
      console.warn('Error accessing localStorage:', error);
    }
  }

  setLangFromCloudflare() {
    this.getUserLocationFromCloudflare().subscribe({
      next: (location: string) => {
        const loc = location.toLowerCase()
        this.checkAndSetSelectedLanguage(loc)
      },
      error: err => {
        // console.error('Error fetching user location:', err);
        this.checkAndSetSelectedLanguage('en')
      }
    })
  }


  getUserLocationFromCloudflare(): Observable<string> {
    return this.http.get('https://www.cloudflare.com/cdn-cgi/trace', { responseType: 'text' }).pipe(
      map((response: string) => {
        let data = response.replace(/[\r\n]+/g, '","').replace(/\=+/g, '":"');
        data = '{"' + data.slice(0, data.lastIndexOf('","')) + '"}';
        const jsonData = JSON.parse(data);
        return jsonData.loc;
      }),
      catchError(error => {
        // console.error('Error:', error);
        return of('else'); // Return a fallback value in case of error
      })
    )
  }

  setUserSelectedLang(language: string) {
    //sets language from dropdown + userObject, landings (landingService.getLanguageFromUrl uses langOptions)
    if (this.currentLang() !== language) {

      this.currentLang.set(language);
      this.translate.use(language);

      try {
        localStorage.setItem('lang', language ?? 'en');
      } catch (error) {
        console.warn('Error accessing localStorage:', error);
      }

      //todo rtl design on core
      if (this.configs.hasOnlyLandings) {
        let htmlTag = this.document.getElementsByTagName('html')[0] as HTMLHtmlElement;
        htmlTag.dir = language === 'ar' ? 'rtl' : 'ltr';
      }

    }
  }

  getUserLocale(language: string) {
    //for registration - userLocale
    if (language !== 'en' && this.supportedLanguages.includes(language)) {
      return `${language}_${language.toUpperCase()}`
      // } else {
      // console.log(`lang is not supported or it is already english: ${language}`)
    }
    return 'en_US'
  }

}


